import { SprintAPI } from './api';
import { getActiveWorkspaceId } from './api/workspaceContext';
import {
  markPendingSprintRename,
  peekPendingSprintRename,
} from './pendingSprintRenames';

/**
 * Lock a nested campaign's name to its container mission.
 *
 * The in-memory campaign is often a metadata shell. `onUpdateSprint` updates
 * the pill immediately, but the save layer drops shells (no tasks array), so
 * opening the campaign reloads the previous name. When that happens, fetch the
 * full record and write the name onto it.
 */
export async function syncNestedCampaignName(nestedSprint, newName, { onUpdateSprint } = {}) {
  if (!nestedSprint?.id || typeof newName !== 'string') return;

  const updatedAt = new Date().toISOString();

  if (Array.isArray(nestedSprint.tasks)) {
    onUpdateSprint?.({ ...nestedSprint, name: newName, updatedAt }, { immediate: true });
    return;
  }

  markPendingSprintRename(nestedSprint.id, newName);
  // Pill updates now. The save of this shell is skipped — the fetch below persists.
  onUpdateSprint?.({ ...nestedSprint, name: newName, updatedAt });

  try {
    const full = await SprintAPI.get(nestedSprint.id, { workspaceId: getActiveWorkspaceId() });
    if (!full) return;
    if (peekPendingSprintRename(nestedSprint.id) !== newName) return;

    const record = {
      ...full,
      id: full.id || nestedSprint.id,
      name: newName,
      tasks: Array.isArray(full.tasks) ? full.tasks : [],
      categories: full.categories || [],
      updatedAt: new Date().toISOString(),
    };

    if (peekPendingSprintRename(nestedSprint.id) !== newName) return;

    if (onUpdateSprint) {
      onUpdateSprint(record, { immediate: true });
    } else {
      await SprintAPI.update(record.id, record, { workspaceId: getActiveWorkspaceId() });
    }
    // Leave the pending mark until a server load comes back with this name.
    // Clearing here races an in-flight fetch that still has the old name.
  } catch (error) {
    console.error('Failed to sync nested campaign name:', error);
  }
}
